import { createAsyncThunk } from "@reduxjs/toolkit";
import type { OrderException } from "@store/types/OrderExceptions.types";
import { OrderExceptionCode } from "@store/types/OrderExceptions.types";

import type { Address } from "@jti/fe-common";
import { customPaymentMethods } from "@jti/fe-common";
import type { IOrderAttributeInput } from "@jti/magento";
import { getCurrentMagentoClient } from "@jti/magento";
import type { MagentoGraphQLRequestClientError } from "@jti/magento-api-v2";

export type PlaceOrderPayload = {
  cartId: string;
  additionalAttributes: IOrderAttributeInput[];
  shippingAddress: Address;
  isZeroValueOrder?: boolean;
};

export type PlaceOrderResult = {
  orderNumber: string;
  entityId: string;
  shippingAddress: Address;
};

export const placeOrderDeclaration = (isFreeTrial: boolean) =>
  createAsyncThunk<PlaceOrderResult, PlaceOrderPayload, { rejectValue: OrderException }>(
    isFreeTrial ? "freeTrialCheckout/placeOrder" : "checkout/placeOrder",

    async (
      { cartId, shippingAddress, additionalAttributes, isZeroValueOrder = false }: PlaceOrderPayload,
      { rejectWithValue }
    ) => {
      try {
        const orderDetails = await getCurrentMagentoClient(isFreeTrial).LEGACY__placeOrder(
          cartId,
          additionalAttributes,
          isZeroValueOrder ? customPaymentMethods.zeroValueOrder : undefined
        );

        if (orderDetails.errors?.length) {
          return rejectWithValue({
            message: orderDetails.errors[0].message,
            code: OrderExceptionCode.MagentoOrderCreationIssue,
          });
        }

        if (!orderDetails.orderNumber || !orderDetails.entityId) {
          throw new Error("Order number and/or entity id is missing");
        }

        return { orderNumber: orderDetails.orderNumber, entityId: orderDetails.entityId, shippingAddress };
      } catch (error) {
        const errorMessage =
          (error as MagentoGraphQLRequestClientError).response?.errors?.[0]?.message || (error as Error).message;

        return rejectWithValue({
          message: errorMessage,
          code: OrderExceptionCode.MagentoOrderCreationIssue,
        });
      }
    }
  );

/**
 * @deprecated
 * commerce will stop using it as soon as new checkout is fully launched
 */
export const placeOrder = placeOrderDeclaration(false);

/**
 * @deprecated
 * commerce will stop using it as soon as new checkout is fully launched
 */
export const placeFreeTrialOrder = placeOrderDeclaration(true);
